import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import MemoryRoundedIcon from "@mui/icons-material/MemoryRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import BatteryChargingFullRoundedIcon from "@mui/icons-material/BatteryChargingFullRounded";

function getStatusStyle(status) {
  const value = String(status || "").toUpperCase();

  switch (value) {
    case "LOW_FLOW":
      return {
        chipBg: "#fff7ed",
        chipColor: "#c2410c",
        chipBorder: "#fdba74",
      };
    case "HIGH_FLOW":
      return {
        chipBg: "#fee2e2",
        chipColor: "#b91c1c",
        chipBorder: "#fca5a5",
      };
    default:
      return {
        chipBg: "#dcfce7",
        chipColor: "#166534",
        chipBorder: "#86efac",
      };
  }
}

export default function DeviceTelemetryCard({ item }) {
  const statusStyle = getStatusStyle(item?.status);
  const battery = Number(item?.battery || 0);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid #e2e8f0",
      }}
    >
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          mb={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 3,
                bgcolor: "#0f2f35",
                color: "#fff",
                display: "grid",
                placeItems: "center",
              }}
            >
              <MemoryRoundedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a" }}>
                {item?.device_id || "Unknown Device"}
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                {item?.topic || "--"}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={item?.status || "UNKNOWN"}
            sx={{
              bgcolor: statusStyle.chipBg,
              color: statusStyle.chipColor,
              border: `1px solid ${statusStyle.chipBorder}`,
              fontWeight: 700,
            }}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography sx={{ color: "#64748b", fontWeight: 700 }}>Flow In</Typography>
                  <WaterDropRoundedIcon sx={{ color: "#0369a1" }} />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {item?.flow_in ?? "--"}
                </Typography>
                <Typography sx={{ color: "#64748b" }}>drops/min</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography sx={{ color: "#64748b", fontWeight: 700 }}>Flow Out</Typography>
                  <WaterDropRoundedIcon sx={{ color: "#16a34a" }} />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {item?.flow_out ?? "--"}
                </Typography>
                <Typography sx={{ color: "#64748b" }}>ml/hr</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" mb={1}>
                  <Typography sx={{ color: "#64748b", fontWeight: 700 }}>Battery</Typography>
                  <BatteryChargingFullRoundedIcon sx={{ color: "#c2410c" }} />
                </Stack>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  {battery}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, battery))}
                  sx={{ height: 10, borderRadius: 999, bgcolor: "#e2e8f0" }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}